import * as React from 'react';
import styled, { DefaultTheme, css } from 'styled-components';
import { memoizeFunction, isString, isNumber } from '@agilepoint/utils-common';
import { excludeKeys } from '../../utils';
import { ITextareaProps } from './textarea-props';
import { TextareaState } from './theme';
import { selectThemeSemantics } from '../../styling';
import { typographyPresets } from '../typography';

const TextareaBase = React.memo((props: ITextareaProps) => {
    const { elementRef, onValueChange, onChange, hidden, maxLength, value, defaultValue, disabled, rows } = props;
    const textareaProps = React.useMemo(() => excludeKeys(props, 'label', 'resize', 'children', 'elementRef', 'onValueChange', 'error'), [props]);
    const _onChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = (e.target as HTMLTextAreaElement).value;

        if (onChange !== undefined) {
            onChange(e);
        }

        if (onValueChange !== undefined) {
            onValueChange(newValue);
        }

    }, [onValueChange, onChange]);

    React.useEffect(() => {
        let targetValue = (value || defaultValue || '');
        targetValue = (
            isString(targetValue)
                ? targetValue
                : (isNumber(targetValue)
                    ? targetValue.toString()
                    : targetValue.join()
                )
        );
        if(onValueChange && maxLength && (targetValue.length > maxLength)) {
            onValueChange(targetValue.substring(0, maxLength));
        }
    }, [value, defaultValue, onValueChange, maxLength]);

    return hidden ? null : (
        <textarea
            {...textareaProps}
            value={value}
            defaultValue={defaultValue}
            onChange={_onChange}
            disabled={disabled}
            maxLength={maxLength}
            rows={rows}
            ref={elementRef}
        />
    );
});

TextareaBase.displayName = 'Textarea';

const getTextareaStyleByState = memoizeFunction((state: TextareaState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { textarea: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        color: ${({ theme }) => getStyles(theme).foregroundColor};
    `;
});

const getTextareaStyle = memoizeFunction((state: TextareaState) => ({ disabled, error }: ITextareaProps) => getTextareaStyleByState(disabled ? 'disabled' : (error ? 'error' : state)));

export const Textarea = styled(TextareaBase).attrs(() => ({
    className: String('ap-textarea')
}))`
    border-color: ${({ theme: { textarea: { colors } }, error, disabled }) => (error && !disabled ? colors.error.borderColor : colors.default.borderColor)};
    border-width: ${({ theme: { textarea } }) => textarea.composites.borderWidth};
    border-style: ${({ theme: { textarea } }) => textarea.composites.borderStyle};
    border-radius: ${({ theme: { textarea: { composites } } }) => composites.borderRadius};
    box-sizing: border-box;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    font-family: inherit;
    ${typographyPresets.textStyles.small};
    height: auto;
    min-height: 70px;
    padding: ${selectThemeSemantics(({ gap }) => `${gap.l} ${gap.xl}`)};
    resize: ${({ resize }) => (resize ? 'both' : 'none')};
    ${getTextareaStyle('default')};
    &:hover {
        ${getTextareaStyle('hovered')};
    }
    &:focus {
        outline: 0;
        ${getTextareaStyle('focussed')};
    }
`;
